import { esCopyTag } from './EsCopy';
import { esFormatDate } from './EsIntl';

/**
 * Dates as esfront shows them.
 *
 * <p>In English the month names are spelled out here rather than asked of Intl:
 * the format is the app's, not the browser's, and "Sept" in en-GB would make one
 * table read differently on two machines. A reader who chose another language
 * gets that language's own date format through EsIntl.
 */

const MONTHS = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];

// The reader's tag from the copy store, which holds one only while rolloutEsTranslations is on and a
// bundle has loaded. Any English, and no tag at all, keeps the app's own 26-Sep-2026 and its
// spelled-out months. Another language reads its own order and month names from Intl: day-month-
// year with English hyphens is no date format in ja or zh ("26-10月-2026").
const english = () => {
    const aTag = esCopyTag();
    return !aTag || aTag.toLowerCase().startsWith( 'en' );
};

const month = ( pDate ) => MONTHS[ pDate.getMonth() ];

const DAY_MON_YEAR = { dateStyle: 'medium' };
const DAY_MON = { day: 'numeric', month: 'short' };
const MON_YEAR = { month: 'short', year: 'numeric' };
const DAY_MON_YEAR_TIME = { dateStyle: 'medium', timeStyle: 'short' };
const DAY_MON_TIME = { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' };

/**
 * A Date from what the server sends, or null. A bare LocalDate ("2026-09-26")
 * is read as local midnight: new Date() reads that shape as UTC, which west of
 * Greenwich is the evening before.
 */
function toDate( pValue ) {
    if ( !pValue ) return null;

    const aDate = pValue instanceof Date ? pValue
        : new Date( /^\d{4}-\d{2}-\d{2}$/.test( pValue ) ? pValue + 'T00:00:00' : pValue );

    return Number.isNaN( aDate.getTime() ) ? null : aDate;
}

/**
 * 26-Sep-2026, and 1-Aug-2026 with no leading zero. Anything that is not a
 * date answers '' rather than "NaN-undefined-NaN".
 */
export function esDayMonYear( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';
    if ( !english() ) return esFormatDate( aDate, DAY_MON_YEAR );

    return aDate.getDate() + '-' + month( aDate ) + '-' + aDate.getFullYear();
}

/** 26-Sep: a chart axis, where the year is the whole axis's and not each tick's. */
export function esDayMon( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';
    if ( !english() ) return esFormatDate( aDate, DAY_MON );

    return aDate.getDate() + '-' + month( aDate );
}

/** Sep-2026: a month as a whole, such as a cohort of signups. */
export function esMonYear( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';
    if ( !english() ) return esFormatDate( aDate, MON_YEAR );

    return month( aDate ) + '-' + aDate.getFullYear();
}

/** 26-Sep-2026 9:20 PM: the date as above and the clock as en-US short time. */
export function esDayMonYearTime( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';
    if ( !english() ) return esFormatDate( aDate, DAY_MON_YEAR_TIME );

    return esDayMonYear( aDate ) + ' ' + ( aDate.getHours() % 12 || 12 ) + ':'
        + String( aDate.getMinutes() ).padStart( 2, '0' ) + ' '
        + ( aDate.getHours() < 12 ? 'AM' : 'PM' );
}

/**
 * Whether the reader's locale tells the time on twelve hours (h11 or h12) --
 * en-US -- or on twenty-four, as most of Europe does. Asked of the browser on
 * each call, so a test can change the answer; anything that cannot answer
 * reads as twelve, the app's own default.
 */
export function esTwelveHour() {
    try {
        const aCycle = new Intl.DateTimeFormat( esCopyTag() ?? undefined, { hour: 'numeric' } ).resolvedOptions().hourCycle;
        return aCycle !== 'h23' && aCycle !== 'h24';
    } catch {
        return true;
    }
}

/**
 * The chat's clock in the reader's own hour cycle: 3:35a and 11:35p on twelve
 * hours, 03:35 and 23:35 on twenty-four. Every chat time and its hover goes
 * through here.
 *
 * <p><b>Local, and the server's stamp is a LocalDateTime with no zone on it.</b>
 * JavaScript parses a zone-less stamp as local time, which is what is wanted
 * here and is worth knowing rather than discovering: a table playing in two
 * time zones would each see the stamp in their own, and neither would be the
 * time the words were actually said. That is a real bug, and fixing it needs
 * the column to carry an instant, which is a migration.
 */
export function esClock( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';

    const aMinutes = ':' + String( aDate.getMinutes() ).padStart( 2, '0' );
    if ( !esTwelveHour() ) return String( aDate.getHours() ).padStart( 2, '0' ) + aMinutes;

    return ( aDate.getHours() % 12 || 12 ) + aMinutes + ( aDate.getHours() < 12 ? 'a' : 'p' );
}

/**
 * 2:04pm and 11:02am: Members' "online since" clock, which Tim asked for with the meridiem
 * whole (2026-11-01) where the chat's esClock writes a bare 'p'. A reader on twenty-four
 * hours reads 14:04, as esClock gives them.
 */
export function esClockMeridiem( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';
    if ( !esTwelveHour() ) return esClock( aDate );

    return ( aDate.getHours() % 12 || 12 ) + ':' + String( aDate.getMinutes() ).padStart( 2, '0' )
        + ( aDate.getHours() < 12 ? 'am' : 'pm' );
}

/**
 * 26-Sep 12:34p, or 26-Sep-2026 12:34p with pYear: a Chat topic's newest post,
 * on one line. The clock is esClock's, in the reader's hour cycle.
 */
export function esDayMonClock( pValue, pYear ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';
    if ( pYear ) return esDayMonYearClock( aDate );
    if ( !english() ) return esFormatDate( aDate, DAY_MON_TIME );

    return aDate.getDate() + '-' + month( aDate ) + ' ' + esClock( aDate );
}

/** 26-Sep-2026 12:34p: the clock's hover, the whole moment in one short line. */
export function esDayMonYearClock( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';
    if ( !english() ) return esFormatDate( aDate, DAY_MON_YEAR_TIME );

    return esDayMonYear( aDate ) + ' ' + esClock( aDate );
}

/**
 * The local calendar day a moment falls on, 2026-09-26, as a key two moments
 * share only when they fall on the same day: the chat draws a day's rule when
 * this changes, and names the rule's test hook by it. '' for anything that is
 * not a date.
 */
export function esLocalDay( pValue ) {
    const aDate = toDate( pValue );
    if ( !aDate ) return '';

    return aDate.getFullYear() + '-' + String( aDate.getMonth() + 1 ).padStart( 2, '0' ) + '-'
        + String( aDate.getDate() ).padStart( 2, '0' );
}
